import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon, ListGroup, ListRow, Skeleton, TextField } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { MainActionBar } from '../native-controls.tsx';
import type { Nav, Pushed } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { haptic } from '../telegram.ts';

type Offer = Extract<Pushed, { name: 'domainCheckout' }>;

const EMPTY_CONTACT = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: 'RU',
  city: '',
  street: '',
  zipcode: '',
};
type ContactForm = typeof EMPTY_CONTACT;

/** zod field errors from the server, keyed by field name */
function fieldErrors(err: unknown): Partial<Record<keyof ContactForm, string>> {
  const issues = (err as { data?: { zodError?: { fieldErrors?: Record<string, string[]> } } })?.data?.zodError
    ?.fieldErrors;
  if (!issues) return {};
  return Object.fromEntries(Object.entries(issues).map(([k, v]) => [k, v[0]]));
}

export function DomainCheckoutScreen({ nav, offer }: { nav: Nav; offer: Offer }) {
  const trpc = useTRPC();
  const { t, money } = usePrefs();
  const queryClient = useQueryClient();
  const contacts = useQuery(trpc.contacts.list.queryOptions());
  const balance = useQuery(trpc.balance.summary.queryOptions());
  const config = useQuery(trpc.config.queryOptions());
  const ownerRequired = config.data?.domainOwnerRequired ?? true;
  const [form, setForm] = useState<ContactForm>(EMPTY_CONTACT);
  const [orderId, setOrderId] = useState<string | null>(null);

  const createContact = useMutation(trpc.contacts.create.mutationOptions());
  const order = useMutation(
    trpc.domains.order.mutationOptions({
      onSuccess: (o) => {
        setOrderId(o.id);
        if (o.status === 'active') haptic.success();
        queryClient.invalidateQueries(trpc.balance.pathFilter());
        queryClient.invalidateQueries(trpc.domains.pathFilter());
      },
      onError: () => haptic.error(),
    }),
  );
  const status = useQuery({
    ...trpc.orders.get.queryOptions({ id: orderId ?? '00000000-0000-0000-0000-000000000000' }),
    enabled: !!orderId,
    initialData: order.data,
    refetchInterval: (q) => (['paid', 'provisioning'].includes(q.state.data?.status ?? '') ? 3000 : false),
  });

  const contact = contacts.data?.[0];
  const needsContact = ownerRequired && contacts.isSuccess && !contact;
  const shortBy = balance.data ? Math.max(0, offer.price - balance.data.balanceCents) : 0;
  const errors = fieldErrors(createContact.error);
  const set = (k: keyof ContactForm) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!ownerRequired) return order.mutate({ domain: offer.domain });
    let contactId = contact?.id;
    if (!contactId) {
      const c = await createContact.mutateAsync(form).catch(() => null);
      if (!c) return haptic.error();
      contactId = c.id;
      queryClient.invalidateQueries(trpc.contacts.pathFilter());
    }
    order.mutate({ domain: offer.domain, contactId });
  };

  const state = status.data?.status;
  if (orderId && state === 'active') {
    return (
      <main className="ul-screen ul-screen--with-action">
        <div className="ul-success" aria-live="polite">
          <span className="ul-success__mark">
            <Icon name="check" size={32} />
          </span>
          <span className="ul-summary__name">
            {offer.label}
            <b>.{offer.tld}</b>
          </span>
          <span className="ul-success__text">{t.checkout.registered}</span>
        </div>
        <MainActionBar action={{ text: t.checkout.myDomains, onClick: () => nav.reset('domains') }} />
      </main>
    );
  }
  if (orderId && (state === 'paid' || state === 'provisioning' || state === 'manual_review')) {
    return (
      <main className="ul-screen">
        <div className="ul-wait" aria-live="polite">
          <span className="ul-sig ul-sig--provisioning" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="ul-sig__bar is-on"
                style={{ height: 4 + i * 4, animationDelay: `${i * 120}ms` }}
              />
            ))}
          </span>
          <span className="ul-wait__title">{t.checkout.registering(offer.domain)}</span>
          <span className="ul-wait__text">
            {state === 'manual_review' ? t.checkout.manualReview : t.checkout.usuallyMinute}
          </span>
        </div>
      </main>
    );
  }
  if (orderId && state === 'refunded') {
    return (
      <main className="ul-screen ul-screen--with-action">
        <div className="ul-error" role="alert">
          {t.checkout.refused(offer.domain)}
        </div>
        <MainActionBar action={{ text: t.checkout.pickAnother, onClick: () => nav.reset('domains') }} />
      </main>
    );
  }

  const busy = createContact.isPending || order.isPending;
  return (
    <main className="ul-screen ul-screen--with-action">
      <section className="ul-summary" aria-label={t.checkout.domain}>
        <span className="ul-summary__name">
          {offer.label}
          <b>.{offer.tld}</b>
        </span>
        <span className="ul-summary__price">{money(offer.price)}</span>
        <span className="ul-summary__note">
          {t.checkout.firstYear}
          {offer.renew !== null ? `, ${t.checkout.renewal(money(offer.renew))}` : ''}. {t.checkout.noRefund}
        </span>
      </section>

      {config.isPending || (ownerRequired && contacts.isPending) ? (
        <Skeleton height={64} />
      ) : !ownerRequired ? (
        <ListGroup header={t.checkout.owner} footer={t.checkout.ownerUplinkFooter}>
          <ListRow icon="shield" title="Uplink" subtitle={t.checkout.ownerUplinkSubtitle} />
        </ListGroup>
      ) : contact ? (
        <ListGroup header={t.checkout.owner} footer={t.checkout.ownerIcann}>
          <ListRow icon="user" title={contact.name} subtitle={`${contact.email} · ${contact.country}`} />
        </ListGroup>
      ) : (
        <section className="ul-stack" aria-label={t.checkout.owner}>
          <div className="ul-group__header">{t.checkout.owner}</div>
          <div className="ul-form">
            <div className="ul-form__row">
              <TextField
                id="c-first"
                label={t.checkout.firstName}
                value={form.firstName}
                onChange={set('firstName')}
                error={errors.firstName}
                autoComplete="given-name"
              />
              <TextField
                id="c-last"
                label={t.checkout.lastName}
                value={form.lastName}
                onChange={set('lastName')}
                error={errors.lastName}
                autoComplete="family-name"
              />
            </div>
            <TextField
              id="c-email"
              label={t.checkout.email}
              type="email"
              inputMode="email"
              value={form.email}
              onChange={set('email')}
              error={errors.email}
              hint={t.checkout.emailHint}
              autoComplete="email"
            />
            <TextField
              id="c-phone"
              label={t.checkout.phone}
              type="tel"
              inputMode="tel"
              placeholder="+79991234567"
              value={form.phone}
              onChange={set('phone')}
              error={errors.phone}
              autoComplete="tel"
            />
            <div className="ul-form__row">
              <TextField
                id="c-country"
                label={t.checkout.country}
                value={form.country}
                onChange={set('country')}
                error={errors.country}
                hint={t.checkout.countryHint}
                autoComplete="country"
              />
              <TextField
                id="c-zip"
                label={t.checkout.zip}
                inputMode="numeric"
                value={form.zipcode}
                onChange={set('zipcode')}
                error={errors.zipcode}
                autoComplete="postal-code"
              />
            </div>
            <TextField
              id="c-city"
              label={t.checkout.city}
              value={form.city}
              onChange={set('city')}
              error={errors.city}
              autoComplete="address-level2"
            />
            <TextField
              id="c-street"
              label={t.checkout.street}
              value={form.street}
              onChange={set('street')}
              error={errors.street}
              autoComplete="street-address"
            />
          </div>
          <div className="ul-group__footer">{t.checkout.ownerIcannSaved}</div>
        </section>
      )}

      {balance.data ? (
        <ListGroup header={t.checkout.payment}>
          <ListRow icon="wallet" title={t.checkout.fromBalance} value={money(balance.data.balanceCents)} />
        </ListGroup>
      ) : null}

      {order.isError ? (
        <div className="ul-error" role="alert">
          {errorText(order.error)}
        </div>
      ) : null}
      {createContact.isError && Object.keys(errors).length === 0 ? (
        <div className="ul-error" role="alert">
          {errorText(createContact.error)}
        </div>
      ) : null}

      <MainActionBar
        action={
          shortBy > 0
            ? {
                text: t.common.topUpBy(money(Math.max(shortBy, 500))),
                onClick: () =>
                  nav.push({
                    name: 'topup',
                    amountCents: Math.max(shortBy, 500),
                    // Owner details still to fill in: the order needs them first, so no automatic purchase
                    after:
                      ownerRequired && !contact
                        ? undefined
                        : {
                            kind: 'domainOrder',
                            domain: offer.domain,
                            contactId: contact?.id,
                            label: offer.domain,
                          },
                  }),
              }
            : {
                text: t.checkout.buyFor(money(offer.price)),
                onClick: submit,
                loading: busy,
                disabled:
                  !balance.data ||
                  !config.data ||
                  (needsContact && Object.values(form).some((v) => !v.trim())),
              }
        }
      />
    </main>
  );
}
